import React from 'react';

const TermsOfService = () => {
  return (
    <div className="pt-24 pb-20">
      <section className="py-20 bg-gradient-to-br from-gray-50 to-teal-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
            Terms of Service
          </h1>
          <p className="text-gray-600">
            Effective Date: December 23, 2024
          </p>
          <p className="text-gray-600">
            Version: 1.0
          </p>
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 prose prose-lg max-w-none">
          <div className="space-y-8">
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">1. Welcome to NeuPath</h2>
              <p className="text-gray-600 mb-4">
                By accessing and using the services provided by NeuPath ("Company," "we," "our," or "us"), 
                including our website and the I AM Assessment platform, you agree to these Terms of Service ("Terms"). 
                We've designed our services to help organizations understand and navigate change, particularly 
                around AI adoption. If you have questions about these terms, please contact us at support@neupath.ai.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">2. Our Partnership with You</h2>
              <p className="text-gray-600 mb-4">
                NeuPath provides AI readiness assessments and consulting services that help individuals and 
                organizations thrive through transformation. We believe everyone's perspective matters, which is 
                why we focus on the human side of change.
              </p>
              
              <h3 className="text-xl font-semibold text-slate-800 mb-2">Organizations and Individuals</h3>
              <p className="text-gray-600 mb-4">
                If you're using the Service on behalf of your organization, you confirm you have the authority to 
                accept these terms for them. When we say "you," we mean both you as an individual and your 
                organization when applicable.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">3. How We Work Together</h2>
              <p className="text-gray-600 mb-4">
                We gather insights to provide meaningful recommendations that help people thrive through change. 
                Our AI technology analyzes patterns while keeping your individual privacy paramount. For complete 
                details on our privacy-first approach, please see our Privacy Policy.
              </p>
              
              <h3 className="text-xl font-semibold text-slate-800 mb-2">Our Services Include:</h3>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li>AI readiness assessments (I AM Assessment)</li>
                <li>Strategic planning and roadmapping</li>
                <li>Change management consulting</li>
                <li>Implementation support</li>
                <li>Training and enablement services</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">4. Your Commitments</h2>
              <p className="text-gray-600 mb-4">
                To maintain a trusted environment for everyone, you agree to:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4">
                <li>Share authentic information during registration and assessments</li>
                <li>Keep your account credentials secure</li>
                <li>Use our services for their intended purpose - understanding and supporting people through change</li>
                <li>Respect the integrity of our platform and other users</li>
                <li>Comply with all applicable laws and regulations</li>
              </ul>
              
              <h3 className="text-xl font-semibold text-slate-800 mb-2">Protecting Our Community</h3>
              <p className="text-gray-600 mb-2">Please don't:</p>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li>Share or reproduce assessment questions or our proprietary methodologies</li>
                <li>Create multiple accounts to manipulate results</li>
                <li>Use insights in ways that could harm individuals</li>
                <li>Attempt to reverse engineer our unique algorithms</li>
                <li>Interfere with or disrupt our services</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">5. Our Intellectual Property</h2>
              <p className="text-gray-600 mb-4">
                The I AM Assessment and our consulting frameworks represent years of research and development in 
                understanding how people navigate change. All content, methodologies, algorithms, profile types, 
                and frameworks are proprietary to NeuPath and protected by intellectual property laws. This includes 
                but is not limited to:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4">
                <li>Our unique profile classifications (Pathfinder, Process Architect, Team Guardian, Proof Seeker)</li>
                <li>Assessment questions and scoring methodologies</li>
                <li>Journey stage calculations and algorithms</li>
                <li>All analytical frameworks and models</li>
                <li>Website content, graphics, and design elements</li>
              </ul>
              <p className="text-gray-600 mb-4">
                You may not reproduce, distribute, modify, or create derivative works from any of our proprietary 
                content without explicit written permission.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">6. Keeping Things Confidential</h2>
              <p className="text-gray-600 mb-4">
                We trust you with valuable insights, and we ask that you treat them with care. Assessment results 
                and consulting deliverables are confidential and meant to support positive change, not to be shared 
                publicly or used competitively.
              </p>
              
              <h3 className="text-xl font-semibold text-slate-800 mb-2">Your Results</h3>
              <p className="text-gray-600 mb-4">
                While our methodologies remain proprietary, the insights generated for you or your organization 
                belong to you. We may use anonymized, aggregated patterns to improve our service and support 
                more people effectively.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">7. Professional Services</h2>
              <p className="text-gray-600 mb-4">
                Our consulting services are advisory in nature. While we strive to provide accurate and helpful 
                guidance based on extensive research and experience, the ultimate responsibility for business 
                decisions and their outcomes rests with you. Our recommendations should not be construed as 
                guarantees of specific results.
              </p>
              
              <h3 className="text-xl font-semibold text-slate-800 mb-2">Understanding Our Role</h3>
              <p className="text-gray-600 mb-4">
                The I AM Assessment provides research-based insights from self-reported data. These insights are 
                powerful tools for understanding and supporting people, but they're one input among many in your 
                decision-making. Transformation success comes from how you apply these insights with your unique 
                understanding of your people.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">8. Service Evolution and Feature Availability</h2>
              <p className="text-gray-600 mb-4">
                Our services are continuously evolving to better serve your needs. Features, capabilities, and 
                services described on our website, in marketing materials, or during consultations may be modified, 
                enhanced, or discontinued at any time without prior notice.
              </p>
              
              <h3 className="text-xl font-semibold text-slate-800 mb-2">Beta and Preview Features</h3>
              <p className="text-gray-600 mb-4">
                We're excited to share what we're working on! Features marked as "Beta," "Preview," or "Coming Soon" 
                are in active development. While we intend to deliver these features and are actively working on them, 
                their final form may differ based on user feedback, technical considerations, or market needs. Beta 
                features may have limited functionality or change significantly before general release. We appreciate 
                your understanding that innovation requires flexibility, and we're committed to delivering value 
                that meets or exceeds your expectations.
              </p>
              
              <h3 className="text-xl font-semibold text-slate-800 mb-2">Service Modifications</h3>
              <p className="text-gray-600 mb-4">
                We reserve the right to modify, suspend, or discontinue any aspect of our services at any time, 
                including but not limited to features, pricing, availability, and system requirements. While we 
                strive to provide advance notice of significant changes, we are not obligated to do so.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">9. AI Technology Limitations</h2>
              <p className="text-gray-600 mb-4">
                Our services leverage artificial intelligence and machine learning technologies to provide insights 
                and recommendations. While we continuously work to improve accuracy and reliability, it's important 
                to understand the inherent limitations of AI technology.
              </p>
              
              <h3 className="text-xl font-semibold text-slate-800 mb-2">AI Accuracy and Reliability</h3>
              <p className="text-gray-600 mb-4">
                AI-generated insights, recommendations, and analysis are probabilistic in nature and may not always 
                be accurate, complete, or appropriate for your specific situation. AI systems can produce unexpected 
                results, may reflect biases present in training data, and should not be relied upon as the sole 
                basis for critical decisions. Always apply professional judgment and consider multiple perspectives 
                when using AI-generated content.
              </p>
              
              <h3 className="text-xl font-semibold text-slate-800 mb-2">Human Oversight Required</h3>
              <p className="text-gray-600 mb-4">
                All AI-generated outputs should be reviewed and validated by qualified professionals before 
                implementation. We strongly recommend maintaining human oversight of all AI-assisted processes and 
                decisions. You remain fully responsible for how you interpret and apply any AI-generated insights 
                or recommendations.
              </p>
              
              <h3 className="text-xl font-semibold text-slate-800 mb-2">No Guarantee of Outcomes</h3>
              <p className="text-gray-600 mb-4">
                While our AI models are based on extensive research and data, we cannot guarantee specific outcomes, 
                success rates, or results. The effectiveness of our recommendations depends on numerous factors 
                outside our control, including but not limited to your organization's unique context, implementation 
                quality, and external market conditions.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">10. Acceptable Use of AI Features</h2>
              <p className="text-gray-600 mb-4">
                Our AI features, including the AI Change Coach, assessment analysis, and any conversational AI 
                interfaces, are designed specifically to support organizational change management and AI readiness 
                assessment. These features must be used solely for their intended purpose within our platform.
              </p>
              
              <h3 className="text-xl font-semibold text-slate-800 mb-2">Prohibited Uses</h3>
              <p className="text-gray-600 mb-2">You expressly agree NOT to:</p>
              <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4">
                <li>Attempt to use our AI features for any purpose other than change management and assessment</li>
                <li>Try to "jailbreak," manipulate, or circumvent AI safety measures or intended functionality</li>
                <li>Use our AI to generate content unrelated to organizational change or AI readiness</li>
                <li>Extract, reverse engineer, or replicate our AI models or training approaches</li>
                <li>Automate interactions with our AI through scripts, bots, or other automated means</li>
                <li>Share AI-generated insights publicly without authorization</li>
                <li>Use our AI features to process data from organizations you don't represent</li>
              </ul>
              
              <h3 className="text-xl font-semibold text-slate-800 mb-2">Monitoring and Enforcement</h3>
              <p className="text-gray-600 mb-4">
                We actively monitor AI usage patterns to ensure compliance with these terms. Any attempt to misuse 
                our AI features, including prompt injection, unauthorized use cases, or attempts to access 
                functionality outside the platform's scope, will result in immediate account suspension or 
                termination at our sole discretion. We reserve the right to review AI interactions and take 
                appropriate action to protect our platform integrity and other users.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">11. Honest Expectations</h2>
              <p className="text-gray-600 mb-4">
                We provide our services with deep care and expertise, but "as is" without warranties of any kind, 
                either express or implied, including but not limited to implied warranties of merchantability, 
                fitness for a particular purpose, or non-infringement.
              </p>
              
              <h3 className="text-xl font-semibold text-slate-800 mb-2">Limitation of Liability</h3>
              <p className="text-gray-600 mb-4">
                To the fullest extent permitted by law, NeuPath shall not be liable for any indirect, incidental, 
                special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred 
                directly or indirectly, or any loss of data, use, goodwill, or other intangible losses. What we 
                can promise is our commitment to helping people navigate change successfully.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">11. If Things Don't Work Out</h2>
              <p className="text-gray-600 mb-4">
                While we hope for long partnerships, we reserve the right to suspend or terminate access if these 
                Terms are violated or if actions harm our community. We'll always try to resolve issues 
                collaboratively first. Upon termination, your right to use our services will immediately cease.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">12. Protecting Each Other</h2>
              <p className="text-gray-600 mb-4">
                You agree to defend, indemnify, and hold harmless NeuPath and its officers, directors, employees, 
                and agents from and against any claims, liabilities, damages, judgments, awards, losses, costs, 
                expenses, or fees arising out of or relating to your violation of these Terms or your use of our 
                services. This helps us focus on what we do best - supporting people through change.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">13. Your Data, Your Control</h2>
              <p className="text-gray-600 mb-4">
                We keep assessment data for 2 years to support your ongoing transformation journey. You can request 
                deletion of your personal data anytime by contacting support@neupath.ai. We'll honor your request 
                while meeting any legal obligations. For more details, see our Privacy Policy.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">14. Legal Framework</h2>
              <p className="text-gray-600 mb-4">
                These Terms shall be governed by and construed in accordance with the laws of Ohio, USA, without 
                regard to its conflict of law provisions. We chose Ohio not just because it's our home, but because 
                of its balanced approach to business and individual rights. Any disputes arising under these Terms 
                shall be subject to the exclusive jurisdiction of the courts in Ohio.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">15. Growing Together</h2>
              <p className="text-gray-600 mb-4">
                As we learn and improve, we may update these Terms. We'll notify you of significant changes, and 
                your continued use means you accept the updates. We're committed to transparency in any changes we make.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">16. Severability</h2>
              <p className="text-gray-600 mb-4">
                If any provision of these Terms is held to be invalid or unenforceable, the remaining provisions 
                shall continue in full force and effect.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">17. Let's Talk</h2>
              <p className="text-gray-600 mb-4">
                Questions? Concerns? Ideas? We're real people who care about your success:
              </p>
              <div className="bg-gray-50 p-6 rounded-lg">
                <p className="text-gray-700">
                  <strong>NeuPath</strong><br />
                  Email: <a href="mailto:support@neupath.ai" className="text-teal-600 hover:text-teal-700">support@neupath.ai</a><br />
                  Website: <a href="https://www.neupath.ai" className="text-teal-600 hover:text-teal-700">www.neupath.ai</a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsOfService;